/**
 * weeklySpiffs.ts — SINGLE SOURCE OF TRUTH for the weekly SPIFF program.
 *
 * Aug 20, 2026. Before this module the program rules were copy-pasted into
 * WeeklySpiffsCard (crew board), SpifsTab (Chad's Ops Review), and
 * GiveSpiffModal (manual awards) — three implementations that drifted, and
 * Chad's copy was computing off `deals.appointment_date` /
 * `claim_called_in_date` (stale columns, often blank or set to the adjuster
 * meeting date) while the crew board used `crew_daily_points`. Same program,
 * different numbers. Everything client-side now imports from here.
 *
 * The ONE deliberate duplicate that remains is the `spiff-weekly-summary` edge
 * function (Deno — can't import from src/). Its header comment says so; if the
 * rules change, change both.
 *
 * Data source is `crew_daily_points`, the ET-bucketed crew activity log — so a
 * claim logged at 11pm ET counts for that day.
 *
 * FRACTIONS ARE REAL: a split deal credits 0.5 to each rep, and the feed's
 * columns are numeric. Counts are deliberately NOT floored. For the three
 * integer-threshold SPIFFs flooring was always a no-op on the gate anyway
 * (floor(x) >= t is identical to x >= t when t is an integer), and Perfect Week
 * now *needs* the halves: Greg's Aug 21, 2026 rule is 4 TOTAL across claims and
 * signed IAs, "whether it is 2 claims and 2 signed ia's or 3.5 signed ia's and
 * .5 claims" — so the components must be summed before any rounding. Flooring
 * each one first would score that example 3, not 4.
 *
 * "Signed IA" is the `build_scheduled` activity kind (`crew_daily_points
 * .builds_scheduled`) — the label the crew app has used since Jun 2026. It is
 * NOT the `builds` column, which is Chad-approved builds on the calendar.
 *
 * Oct 19, 2026: the 5-Star Review SPIFF (FIVE_STAR_REVIEW_SPIFF below) joins the
 * week. It is paid per verified review, not on a threshold, so it lives beside
 * SPIFF_PROGRAM rather than in it. See its own comment.
 */

export type SpiffKey = "monday" | "tuesday" | "hump" | "perfect";

export interface SpiffProgramEntry {
  key: SpiffKey;
  name: string;
  prizeCents: number;
  /**
   * The counter value that earns the spiff within its window (the counter is
   * per-key: claimsMon / apptsTue / claimsWed / perfectTotal). This is THE
   * threshold — computeSpiffSummary and every progress display read it from
   * here so a program change stays a one-entry edit.
   */
  target: number;
  /** Tile-sized rule text. */
  shortRule: string;
  /** Full rule for tooltips / footers / the manual-award modal. */
  rule: string;
  /** Day-of-week the window closes (1=Mon … 6=Sat). */
  cutoffDow: number;
}

/** Perfect Week: claims + signed IAs must reach this, in any mix. */
export const PERFECT_WEEK_TARGET = 4;

/** The program, in the order it plays out across the week. */
export const SPIFF_PROGRAM: SpiffProgramEntry[] = [
  {
    key: "monday",
    name: "Monday Madness",
    prizeCents: 5000,
    target: 1,
    shortRule: "1 claim by Mon EOD",
    rule: "1 claim by Mon EOD",
    cutoffDow: 1,
  },
  {
    key: "tuesday",
    name: "Terrific Tuesday",
    prizeCents: 5000,
    target: 2,
    shortRule: "2 appts by Tue EOD",
    rule: "2 appts week-to-date by Tue EOD",
    cutoffDow: 2,
  },
  {
    key: "hump",
    name: "Hump Day",
    prizeCents: 10000,
    target: 3,
    shortRule: "3 claims by Wed EOD",
    rule: "3 claims by Wed EOD",
    cutoffDow: 3,
  },
  {
    key: "perfect",
    name: "Perfect Week",
    prizeCents: 25000,
    target: PERFECT_WEEK_TARGET,
    shortRule: "4 claims + signed IAs by Sat",
    rule: "4 total across claims and signed IAs by Sat EOD, in any mix",
    cutoffDow: 6,
  },
];

export const SPIFF_KEYS: SpiffKey[] = SPIFF_PROGRAM.map((p) => p.key);

export const SPIFF_BY_KEY: Record<SpiffKey, SpiffProgramEntry> = SPIFF_PROGRAM.reduce(
  (acc, p) => {
    acc[p.key] = p;
    return acc;
  },
  {} as Record<SpiffKey, SpiffProgramEntry>,
);

export const SPIFF_TITLES: Record<SpiffKey, string> = SPIFF_PROGRAM.reduce(
  (acc, p) => {
    acc[p.key] = p.name;
    return acc;
  },
  {} as Record<SpiffKey, string>,
);

/**
 * Oct 19, 2026 — the 5-Star Review SPIFF. Greg: "set it up as a spiff that gets
 * paid with the weekly spiffs. also, they should be recognized with the weekly
 * spiffs." Asked whether it should replace the 5★ commission point: "keep the
 * same pay out. we just want to recognize it weekly and for it to pay out with
 * spiffs."
 *
 * So the money is the 5★ commission point — on a new-structure deal (signed
 * on/after Jul 20, 2026, $500 or less in credits) a verified review lifts a
 * rep's line one point: 11→12% on a full-scope $20k+ job, 10→11% on every other
 * job. Jobs under $20k earn it too (Greg, the same day: "even if job is under
 * $20k, a five star review 1% is still achievable"). 12% is the ceiling, so
 * nothing for achieved-rate reps. For a review verified on
 * or after `startsAt` the commission line pays the pre-review rate
 * (dealRateInfo in CommissionBoard) and the point goes out as this SPIFF in the
 * Tuesday run, so it is paid exactly once. `startsAt` is Mon Oct 19, 2026
 * 00:00 ET; Hoffer's Oct 6 review keeps the point inside the commission.
 *
 * Per review, not a threshold: there is no fixed prize. The amount per rep is
 * computed in the database — five_star_review_point(), the SQL mirror of the
 * commission tiers — and arrives on each five_star_review_credits() row. The
 * amount is a commission figure (1% of a rep's share of a job), so the RPC
 * returns it only for rows the viewer may see that rep's commissions for
 * (self, TL team, ASM downline, finance tier); `amount_cents` is null
 * otherwise, and review SPIFF rows in `spiffs` are hidden the same way. The ⭐
 * itself is public: every verified review is recognized, paid or not.
 *
 * The review week runs from Monday noon ET to the next Monday noon, not
 * Mon..Sat like the threshold windows (Greg, Oct 19: "if chad approves by noon
 * on Monday, they will get paid same week with the spiffs from prior week"). A
 * review verified Monday morning is recognized and paid with the week that just
 * finished, in that Tuesday's run; one verified Monday afternoon waits for the
 * next week. reviewPayWeek() is the rule. Deliberately NOT in SPIFF_PROGRAM (no
 * threshold to race) and never a Give-a-SPIFF preset — the Tuesday run books it
 * from the deal, so a manual award would pay the same review twice. No ASM
 * override on it.
 *
 * The `spiff-weekly-summary` edge function carries the same name, start and
 * Monday-noon cutoff — change them together. The name is also its idempotency
 * key with the deal, so never rename it without migrating the booked rows.
 */
export const FIVE_STAR_REVIEW_SPIFF = {
  name: "5-Star Review",
  /** First review week covered (an ET Monday). */
  startsWeekOf: "2026-10-19",
  /** The same moment as an instant: Mon Oct 19, 2026 00:00 ET (EDT). */
  startsAt: "2026-10-19T04:00:00Z",
  /** A review verified before this hour (ET) on a Monday pays with the week before. */
  mondayCutoffHourET: 12,
  shortRule: "each verified 5★ review · its commission point, paid as a SPIFF",
  rule: "each 5-star homeowner review verified that week — through noon the next Monday (ET) — is recognized here, and the commission point it unlocks (1% of the rep's share of a job signed since Jul 20 with $500 or less in credits, any size; never above 12%, so not achieved-rate reps) pays as a SPIFF in that Monday's run (paid Tuesday) instead of inside the commission",
} as const;

/** Program summary line used in board footers + the weekly email. */
export const SPIFF_PROGRAM_SUMMARY = [
  ...SPIFF_PROGRAM.map((p) => `${p.name} ($${p.prizeCents / 100}): ${p.rule}`),
  `${FIVE_STAR_REVIEW_SPIFF.name}: ${FIVE_STAR_REVIEW_SPIFF.rule}`,
].join(". ");

/** Why a verified review pays no SPIFF — five_star_review_point()'s reason codes. */
export function reviewReasonText(reason: string | null | undefined): string | null {
  switch (reason) {
    case null:
    case undefined:
      return null;
    case "before_start":
      return "reviewed before Oct 19 — the 5★ point pays inside the commission";
    case "legacy":
      return "not on the Jul 20 commission structure";
    case "credits_over_500":
      return "credits over $500";
    case "achieved_rate":
      return "flat achieved rate";
    case "hand_set":
      return "commission set by hand";
    case "inactive":
      return "not on the active roster";
    case "paid_in_commission":
      return "already paid inside the commission";
    case "zero":
      return "no commissionable revenue on the job yet";
    default:
      return "no SPIFF";
  }
}

/** "⭐" for one review, "½⭐" for a split half, "1½⭐", "2⭐"… */
export function fmtReviewStars(credit: number): string {
  const whole = Math.floor(credit + 1e-9);
  const half = credit - whole >= 0.5 - 1e-9;
  const lead = whole === 0 ? (half ? "½" : "") : whole === 1 && !half ? "" : `${whole}${half ? "½" : ""}`;
  return `${lead}⭐`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Date helpers — everything is ET, because crew_daily_points.day is ET-bucketed
// ─────────────────────────────────────────────────────────────────────────────

/** ET calendar date (YYYY-MM-DD) for any JS Date. */
export function etDate(d: Date): string {
  return d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

/** Add n days to a YYYY-MM-DD string. Safe across DST. */
export function addDaysISO(dateStr: string, n: number): string {
  const d = new Date(dateStr + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The ET Monday on or before `d`. */
export function mondayOf(d: Date = new Date()): string {
  const iso = etDate(d);
  const dow = new Date(iso + "T12:00:00Z").getUTCDay(); // 0=Sun..6=Sat
  return addDaysISO(iso, dow === 0 ? -6 : 1 - dow);
}

/** 0=Sun..6=Sat in ET. */
export function todayDowET(): number {
  return new Date(etDate(new Date()) + "T12:00:00Z").getUTCDay();
}

/** Has this SPIFF's window already closed? (Sunday counts as pre-week.) */
export function windowClosed(spiff: SpiffKey, dow: number = todayDowET()): boolean {
  if (dow === 0) return false; // Sunday — next week hasn't started
  return dow > SPIFF_BY_KEY[spiff].cutoffDow;
}

/**
 * The SPIFF week (its ET Monday) a 5-star review is recognized and paid in.
 * Reviews count from Monday noon ET to the next Monday noon, so one verified
 * before noon on a Monday pays in that Tuesday's run with the week just
 * finished. Moving the instant back 12 hours turns Monday noon into Monday
 * 00:00, and mondayOf() does the rest — exact for America/New_York, whose clock
 * changes happen at 2 AM on a Sunday, never between Monday 00:00 and noon.
 */
export function reviewPayWeek(verifiedAt: string | number | Date): string {
  const t =
    verifiedAt instanceof Date ? verifiedAt.getTime() : typeof verifiedAt === "number" ? verifiedAt : Date.parse(verifiedAt);
  return mondayOf(new Date(t - FIVE_STAR_REVIEW_SPIFF.mondayCutoffHourET * 3_600_000));
}

/**
 * ET dates to ask five_star_review_credits() for to cover one pay week: its
 * Monday through the next Monday (that morning, before noon, is still this
 * week). Filter the rows with reviewCountsInWeek().
 */
export function reviewCreditDays(weekMonday: string): { from: string; to: string } {
  return { from: weekMonday, to: addDaysISO(weekMonday, 7) };
}

/** Does this review count in `weekMonday`'s SPIFFs — verified since the SPIFF started, in that pay week? */
export function reviewCountsInWeek(row: { verified_at: string }, weekMonday: string): boolean {
  const t = Date.parse(row.verified_at);
  if (!Number.isFinite(t) || t < Date.parse(FIVE_STAR_REVIEW_SPIFF.startsAt)) return false;
  return reviewPayWeek(t) === weekMonday;
}

/** "Aug 17 – Aug 22" for a week's Monday. */
export function weekRangeLabel(mondayISO: string): string {
  const f = (iso: string) =>
    new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
    });
  return `${f(mondayISO)} – ${f(addDaysISO(mondayISO, 5))}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Computation
// ─────────────────────────────────────────────────────────────────────────────

/** One row of `crew_daily_points`. */
export interface SpiffActivityRow {
  user_id: string;
  day: string; // ET date, "2026-08-17"
  apps: number;
  claims: number;
  /** Signed install agreements — the `build_scheduled` activity kind. */
  builds_scheduled: number;
  builds: number;
}

/** One row of five_star_review_credits(): one verified review's credit to one rep. */
export interface ReviewCreditRow {
  deal_id: string;
  user_id: string;
  /** Recognition credit: 1 on a solo deal, 0.5 for each half of a split. */
  share: number;
  verified_at: string;
  /** ET date the review was verified (YYYY-MM-DD). */
  verified_day: string;
  /** SPIFF cents for this rep, or null when the viewer may not see this rep's pay. */
  amount_cents: number | null;
  /** Why amount_cents is 0 (reviewReasonText); null when it pays, or when hidden. */
  reason: string | null;
}

/** Minimal shape needed from the roster — `TeamMember` satisfies it. */
export interface SpiffEligibleMember {
  id: string;
  full_name: string | null;
}

export interface SpiffStat {
  id: string;
  name: string;
  /** Claims logged on Monday (Monday Madness). */
  claimsMon: number;
  /** Appts Mon..Tue — week-to-date, not Tuesday alone (Terrific Tuesday). */
  apptsTue: number;
  /** Claims Mon..Wed (Hump Day). */
  claimsWed: number;
  /** Claims Mon..Sat (counts toward Perfect Week). */
  claimsSat: number;
  /** Signed IAs Mon..Sat (counts toward Perfect Week). */
  signedIaSat: number;
  /** claimsSat + signedIaSat — the Perfect Week number, unrounded. */
  perfectTotal: number;
  earned: Record<SpiffKey, boolean>;
  /** 5★ review credit this week (Monday noon to Monday noon) — 1 per solo review, 0.5 per split half. */
  reviews: number;
  /**
   * Dollars from the 5-Star Review SPIFF this week, counting only the rows the
   * viewer may see (their own, their team's for a TL, everyone for finance).
   * Private: show it to the rep themselves, never on a public tile.
   */
  reviewEarned: number;
  /** True when every one of this rep's review rows carried a visible amount. */
  reviewAmountKnown: boolean;
  /** Dollars from the four threshold SPIFFs this week — public prizes, reviews NOT included. */
  totalEarned: number;
}

export interface SpiffSummary {
  perRM: Record<string, SpiffStat>;
  list: SpiffStat[];
  counts: Record<SpiffKey, number>;
  rmCount: number;
  /** Reps with 5★ review credit this week. */
  reviewReps: number;
  /** False when the review feed couldn't be read — show "—", never "nobody". */
  reviewsKnown: boolean;
  /** Threshold SPIFF dollars this week (public). Review SPIFFs are private and NOT included. */
  totalPaidOut: number;
}

export function earnedFor(stat: SpiffStat, spiff: SpiffKey): boolean {
  return stat.earned[spiff];
}

/**
 * Derive every rep's weekly SPIFF status from crew activity.
 *
 * `weekMonday` defaults to the current ET week so callers don't have to think
 * about it; pass an explicit Monday to audit a past week.
 *
 * `reviews` are five_star_review_credits() rows covering the week
 * (reviewCreditDays); only those whose pay week is this one count — Monday
 * noon to the next Monday noon (reviewPayWeek). Pass null when that read
 * failed: the summary then says reviewsKnown=false rather than claiming nobody
 * earned one. Rows for anyone outside `members` (ASMs, office, inactive reps)
 * are ignored — the program's eligibility.
 *
 * Review dollars stay out of totalEarned / totalPaidOut: those are shown on
 * public tiles, and a review SPIFF is a rep's commission point. reviewEarned
 * carries the visible part separately.
 */
export function computeSpiffSummary(
  activity: SpiffActivityRow[],
  members: SpiffEligibleMember[],
  weekMonday: string = mondayOf(),
  reviews: ReviewCreditRow[] | null = [],
): SpiffSummary {
  const mondayStr = weekMonday;
  const tuesdayStr = addDaysISO(weekMonday, 1);
  const wednesdayStr = addDaysISO(weekMonday, 2);
  const saturdayStr = addDaysISO(weekMonday, 5);

  const perRM: Record<string, SpiffStat> = {};
  for (const m of members) {
    perRM[m.id] = {
      id: m.id,
      name: m.full_name ?? "—",
      claimsMon: 0,
      apptsTue: 0,
      claimsWed: 0,
      claimsSat: 0,
      signedIaSat: 0,
      perfectTotal: 0,
      earned: { monday: false, tuesday: false, hump: false, perfect: false },
      reviews: 0,
      reviewEarned: 0,
      reviewAmountKnown: true,
      totalEarned: 0,
    };
  }

  // 5-Star Review credits — Monday noon to the next Monday noon. The cents are
  // the database's (the same number the Tuesday run books); a row the viewer
  // may not see carries null. A review verified before the SPIFF started pays
  // inside the commission, so it shows nothing here.
  const reviewCentsById = new Map<string, number>();
  for (const row of reviews ?? []) {
    const stat = perRM[row.user_id];
    if (!stat) continue;
    if (!reviewCountsInWeek(row, weekMonday)) continue;
    const share = Number(row.share ?? 0);
    if (!(share > 0)) continue;
    stat.reviews += share;
    if (row.amount_cents === null || row.amount_cents === undefined) {
      stat.reviewAmountKnown = false;
    } else {
      const cents = Math.max(0, Math.round(Number(row.amount_cents) || 0));
      reviewCentsById.set(row.user_id, (reviewCentsById.get(row.user_id) ?? 0) + cents);
    }
  }

  for (const row of activity) {
    const stat = perRM[row.user_id];
    if (!stat) continue;
    const day = row.day;
    if (day < mondayStr || day > saturdayStr) continue;

    if (day === mondayStr) stat.claimsMon += Number(row.claims ?? 0);
    // Terrific Tuesday counts the week's appts to date, not Tuesday's alone.
    if (day <= tuesdayStr) stat.apptsTue += Number(row.apps ?? 0);
    if (day <= wednesdayStr) stat.claimsWed += Number(row.claims ?? 0);
    stat.claimsSat += Number(row.claims ?? 0);
    stat.signedIaSat += Number(row.builds_scheduled ?? 0);
  }

  const counts: Record<SpiffKey, number> = { monday: 0, tuesday: 0, hump: 0, perfect: 0 };
  let totalPaidOut = 0;
  let reviewReps = 0;

  for (const stat of Object.values(perRM)) {
    // Guard against float drift from repeated 0.5 additions before comparing
    // against a threshold (0.1+0.2 problems bite at exactly 4.0).
    const r2 = (n: number) => Math.round(n * 100) / 100;
    stat.claimsMon = r2(stat.claimsMon);
    stat.apptsTue = r2(stat.apptsTue);
    stat.claimsWed = r2(stat.claimsWed);
    stat.claimsSat = r2(stat.claimsSat);
    stat.signedIaSat = r2(stat.signedIaSat);
    stat.perfectTotal = r2(stat.claimsSat + stat.signedIaSat);

    stat.earned.monday = stat.claimsMon >= SPIFF_BY_KEY.monday.target;
    stat.earned.tuesday = stat.apptsTue >= SPIFF_BY_KEY.tuesday.target;
    stat.earned.hump = stat.claimsWed >= SPIFF_BY_KEY.hump.target;
    // Aug 21, 2026: claims + signed IAs, any mix, halves included.
    stat.earned.perfect = stat.perfectTotal >= SPIFF_BY_KEY.perfect.target;

    for (const p of SPIFF_PROGRAM) {
      if (stat.earned[p.key]) {
        counts[p.key] += 1;
        stat.totalEarned += p.prizeCents / 100;
        totalPaidOut += p.prizeCents / 100;
      }
    }

    stat.reviews = r2(stat.reviews);
    stat.reviewEarned = (reviewCentsById.get(stat.id) ?? 0) / 100;
    if (stat.reviews > 0) reviewReps += 1;
  }

  const list = Object.values(perRM).sort(
    (a, b) =>
      b.totalEarned - a.totalEarned ||
      b.reviews - a.reviews ||
      b.perfectTotal - a.perfectTotal ||
      a.name.localeCompare(b.name),
  );

  return {
    perRM,
    list,
    counts,
    rmCount: members.length,
    reviewReps,
    reviewsKnown: reviews !== null,
    totalPaidOut,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Audit ranges (Chad's Ops Review SPIFF audit board)
// ─────────────────────────────────────────────────────────────────────────────

export type SpiffRange = "30d" | "90d" | "6mo" | "ytd" | "custom";

export const SPIFF_RANGES: Array<{ key: SpiffRange; label: string }> = [
  { key: "30d", label: "Rolling 30" },
  { key: "90d", label: "Rolling 90" },
  { key: "6mo", label: "Last 6 mo" },
  { key: "ytd", label: "YTD" },
  { key: "custom", label: "Custom" },
];

/**
 * Inclusive [from, to] ET date bounds for an audit range.
 * `custom` falls back to the last 30 days until both dates are filled in.
 */
export function spiffRangeBounds(
  range: SpiffRange,
  customFrom?: string,
  customTo?: string,
): { from: string; to: string } {
  const today = etDate(new Date());
  if (range === "custom") {
    if (customFrom && customTo) {
      // Tolerate a backwards range rather than returning nothing.
      return customFrom <= customTo
        ? { from: customFrom, to: customTo }
        : { from: customTo, to: customFrom };
    }
    return { from: addDaysISO(today, -29), to: today };
  }
  if (range === "ytd") return { from: `${today.slice(0, 4)}-01-01`, to: today };
  const days = range === "30d" ? 29 : range === "90d" ? 89 : 181;
  return { from: addDaysISO(today, -days), to: today };
}

/** "Aug 17 – Aug 20, 2026" for a bounds pair. */
export function rangeLabel(from: string, to: string): string {
  const f = (iso: string) =>
    new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
    });
  const year = new Date(to + "T12:00:00Z").getUTCFullYear();
  return `${f(from)} – ${f(to)}, ${year}`;
}
